import { TriangleAlert } from 'lucide-react';
import { useActions, useStrategy, useSafety } from '@/store/selectors';
import { Strategy, PKSource } from '@/types';
import { ExpressionInput, Select } from 'calc-controls';

/** Strategy + infusion duration/start + PK source toggle (PSD §8.1.2 / TODO Task 1.22). */
export function StrategyInfusion(): JSX.Element {
  const actions = useActions();
  const strategy = useStrategy();
  const safety = useSafety();

  return (
    <section className="panel" aria-label="Strategy and infusion" data-testid="strategy-infusion">
      <h2 className="panel-title">Strategy &amp; Infusion</h2>
      <div data-testid="field-strategy">
        <Select
          label="Dosing strategy"
          tooltip="Extended-interval: high-dose once-daily with Hartford nomogram. Traditional: divided doses from Kel/Vd. Synergy: low-dose q12h for Gram-positive synergy."
          options={[
            { value: Strategy.ExtendedInterval, label: 'Extended-interval' },
            { value: Strategy.Traditional, label: 'Traditional' },
            { value: Strategy.Dialysis, label: 'Dialysis' },
            { value: Strategy.Synergy, label: 'Synergy' },
          ]}
          value={strategy.strategy}
          onChange={(v) => actions.setStrategy(v as Strategy)}
        />
      </div>
      <div className="field" title="IV infusion time. Used in one-compartment PK peak/trough predictions. Default 30 min.">
        <ExpressionInput
          id="infusion"
          label="Infusion duration"
          unit="min"
          value={strategy.infusionDurationMin}
          onChange={(v) => actions.setInfusionDuration(v)}
          min={15}
          max={120}
          decimals={0}
          inline
        />
      </div>
      <div className="field">
        <label htmlFor="infusion-start">Infusion start (optional)</label>
        <input
          id="infusion-start"
          type="datetime-local"
          data-testid="field-infusionStart"
          onChange={(e) => actions.setInfusionStart(e.target.value)}
        />
      </div>
      <div className="field">
        <Select
          label="PK source"
          tooltip="Patient-specific PK available after levels (Phase 2)"
          options={[
            { value: PKSource.Population, label: 'Population (default)' },
            { value: PKSource.PatientSpecific, label: 'Patient-specific (needs levels)' },
          ]}
          value={strategy.pkSource}
          disabled
          onChange={(v) => actions.setPKSource(v as PKSource)}
        />
      </div>
      {safety?.eiBlocked && strategy.strategy === Strategy.ExtendedInterval && (
        <div className="badge badge-warn mt-2">
          <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />
          EI blocked — switch to Traditional or Dialysis.
        </div>
      )}
    </section>
  );
}
